import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import AdditionalMessageAlert from 'components/AdditionalMessageAlert';
import EAChoiceSeparator from 'components/EAChoiceSeparator';
import i18next from 'i18next';
import { useContext, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { isAbortError } from 'services/common/ApiUtils';
import { getEcovadisThirdpartyIntegrationID, handleEcovadisApiCallError } from 'services/common/EcovadisUtils';
import { verifyEvDataExistsForThirdparty } from 'services/edge/EcovadisService';
import { Context } from 'states/Store';
import useStyles from 'style/js-style/components/thirdparties/ecovadis/EcovadisStyle';
import EcovadisDialogButtons from '../EcovadisDialogButtons';
import { Trans } from 'react-i18next';

const EvaluationTypeRadioBtn = ({ value, register, label }) => (
  <FormControlLabel
    value={value}
    control={<Radio size="small" color="primary" />}
    {...register('evaluationTypeRadio', { required: true })}
    label={<Typography variant="body2">{label}</Typography>}
  />
);

const EcovadisEvaluationIntegrationIdStep = ({
  onCancel = () => {},
  onValidate = () => {},
}) => {
  const { classes } = useStyles();
  const [state, dispatch] = useContext(Context);
  const [isValidationLoading, setIsValidationLoading] = useState(false);

  const abortCtrlRef = useRef();

  const {
    register,
    getValues,
  } = useFormContext();

  const verifyEvDataExists = async () => {
    setIsValidationLoading(true);
    if (abortCtrlRef.current) {
      abortCtrlRef.current.abort();
    }

    abortCtrlRef.current = new AbortController();
    const signal = abortCtrlRef.current.signal;

    const result = await verifyEvDataExistsForThirdparty(
      state.account.id,
      state.thirdparty.thirdpartyId,
      null,
      signal
    );

    return result.content || null;
  };

  const validateStepHandler = async () => {
    try {
      const evaluationTypeRadioValue = getValues('evaluationTypeRadio');
      if(evaluationTypeRadioValue === 'new') {
        onValidate();
      } else {
        const supplier = await verifyEvDataExists();
        onValidate(supplier);
      }
    } catch (error) {
      if (!isAbortError(error)) {
        handleEcovadisApiCallError(error, state.thirdparty.thirdpartyId, dispatch);
      }
    } finally {
      setIsValidationLoading(false);
    }
  };

  const integrationIdTip = () => {
    return (
      <>
        <Trans i18nKey="ecovadis.requestForm.integrationIdStep.tip1" 
          values={{ integrationId: getEcovadisThirdpartyIntegrationID(state.thirdparty.thirdpartyId) }}>
          X <b>Y</b> Z.
        </Trans>
        <div>
          {i18next.t("ecovadis.requestForm.integrationIdStep.tip2")}
        </div>
      </>
    )
  }

  return (
    <div className={classes.integrationIdStep}>
      <AdditionalMessageAlert
        icon="lightbulb"
        message={integrationIdTip()}
      />

      <RadioGroup
        name="evaluationTypeRadio"
        defaultValue="exists"
        row
      >
        <div className={classes.stepChoices}>
          <div className={classes.importChoice}>
            <EvaluationTypeRadioBtn
              value="exists"
              register={register}
              label={i18next.t(
                'ecovadis.requestForm.integrationIdStep.evaluationExists'
              )}
            />
          </div>
          <div className={classes['separator-vertical']}>
            <EAChoiceSeparator />
          </div>
          <div className={classes.newEvaluationChoice}>
            <EvaluationTypeRadioBtn
              value="new"
              register={register}
              label={i18next.t(
                'ecovadis.requestForm.integrationIdStep.newEvaluation'
              )}
            />
          </div>
        </div>
      </RadioGroup>
      <EcovadisDialogButtons
        onCancel={onCancel}
        onValidate={validateStepHandler}
        isLoading={isValidationLoading}
      />
    </div>
  );
};

export default EcovadisEvaluationIntegrationIdStep;
